import * as React from "react";
import { Upload, Button, Table, Alert, Spin, Breadcrumb } from "antd";
import { UploadOutlined, ImportOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { FetchWithAuth } from "digimaker-ui/util";
import {
  ParsedRow,
  ImportResult,
  ImportState,
  emptyImportState,
  parseExcel,
  buildTableColumns,
} from "./importUtils";

const HEADER_MAP: Record<string, string> = {
  "Student No.": "student_no",
  "Fees 1": "fees_1",
  "Fees Received": "fees_received",
};

const columns = buildTableColumns(HEADER_MAP);

const ImportFinanceRecords: React.FC = () => {
  const [state, setState] = React.useState<ImportState>(emptyImportState());

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result as ArrayBuffer;
      const rows = parseExcel(data, HEADER_MAP);
      setState((s) => ({
        ...s,
        rows,
        fileName: file.name,
        result: null,
        error:
          rows.length === 0 ? "No valid rows found. Check the Excel format." : "",
      }));
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleImport = () => {
    if (state.rows.length === 0) return;
    setState((s) => ({ ...s, loading: true, result: null, error: "" }));
    FetchWithAuth("icd/import/finance-records", {
      method: "POST",
      body: JSON.stringify(state.rows),
    })
      .then((data: any) =>
        setState((s) => ({
          ...s,
          loading: false,
          result: data.data as ImportResult,
        })),
      )
      .catch((err) =>
        setState((s) => ({ ...s, loading: false, error: String(err) })),
      );
  };

  return (
    <div>
      <h2>Import Finance Records</h2>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/">Home</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Import Finance Records</Breadcrumb.Item>
      </Breadcrumb>

      <p style={{ color: "#555", marginTop: 8, marginBottom: 4 }}>
        Upload an Excel file (.xlsx / .xls). The first row must be the header with columns: <strong>Student No.</strong>, <strong>Fees 1</strong>, <strong>Fees Received</strong>.
      </p>

      <div style={{ marginTop: 16 }}>
        <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleFile}>
          <Button icon={<UploadOutlined />}>Choose Excel file</Button>
        </Upload>

        {state.fileName && (
          <span style={{ marginLeft: 12, color: "#666" }}>
            {state.fileName} — {state.rows.length} rows
          </span>
        )}

        {state.error && (
          <Alert type="error" message={state.error} style={{ marginTop: 12 }} />
        )}

        {state.rows.length > 0 && (
          <>
            <Table
              style={{ marginTop: 16 }}
              dataSource={state.rows}
              columns={columns}
              rowKey={(_, i) => String(i)}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
            <Button
              type="primary"
              icon={<ImportOutlined />}
              loading={state.loading}
              onClick={handleImport}
              style={{ marginTop: 8 }}
            >
              Import {state.rows.length} rows
            </Button>
          </>
        )}

        {state.loading && <Spin style={{ marginLeft: 16 }} />}

        {state.result && (
          <Alert
            style={{ marginTop: 16 }}
            type={state.result.errors.length > 0 ? "warning" : "success"}
            message={`Imported ${state.result.imported} rows`}
            description={
              state.result.errors.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {state.result.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
};

export default ImportFinanceRecords;
